import React, { useState } from "react";
import { HelpCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";

/**
 * Phase 22.1 — customer AI Q&A widget for the Product Detail Page.
 *
 * Sends ``POST /api/ai/product/question`` and renders the answer with a
 * clear AI disclaimer. Works for both anonymous and authenticated users.
 * The backend already grounds answers strictly on product data and returns
 * "No lo sé con certeza…" when uncertain.
 */
export default function ProductQAWidget({ productId }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (q.length < 3) {
      toast.error("Escribe una pregunta un poco más larga.");
      return;
    }
    setBusy(true);
    const t = toast.loading("IA pensando…");
    try {
      const { data } = await api.post("/ai/product/question", {
        product_id: productId,
        question: q,
      });
      setAnswer((data.answer || "").trim());
      toast.success("Respuesta lista", { id: t });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        detail === "ai_timeout"
          ? "El servicio de IA está tardando. Inténtalo de nuevo."
          : "IA no disponible en este momento.";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="product-ai-qa"
      className="mt-10 rounded-2xl border border-neutral-200 bg-white p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <HelpCircle className="h-4 w-4 text-[#00953B]" />
        <h3 className="font-black text-lg tracking-tight">
          Pregunta sobre este producto
        </h3>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Responderá una IA basándose únicamente en la información del producto.
      </p>
      <form onSubmit={ask} className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="product-ai-ask-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ej: ¿Es sin gluten? ¿Cuántas raciones trae?"
          className="h-11 rounded-full border-neutral-200"
        />
        <Button
          data-testid="product-ai-ask-btn"
          type="submit"
          disabled={busy}
          className="h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-1 shrink-0"
        >
          <Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Preguntando…" : "Preguntar"}
        </Button>
      </form>
      {answer && (
        <div
          data-testid="product-ai-answer"
          className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-sm text-neutral-800"
        >
          <p className="leading-relaxed">{answer}</p>
          <p className="mt-2 text-[11px] text-neutral-500">
            Respuesta generada por IA basada en la información del producto.
          </p>
        </div>
      )}
    </section>
  );
}
